import React, { useState, useEffect } from 'react';
import {
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    Slider,
    Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import '@fontsource/vazirmatn';
import { api, apiRoutes } from '../../api';
import SortingListings from './SortingListings';
import { selectionContains } from './aiSearchSync';
import './FilterPanel.css';

let filterLookupCache = null;

// Format large numbers in Persian-friendly short form
const fmt = (n) => {
    if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} م`;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)} م`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)} ه`;
    return String(n);
};

// ── Reusable range-slider row ────────────────────────────────────────────────
function RangeSlider({ label, min, max, valueMin, valueMax, onChange, format = fmt }) {
    return (
        <div className="fp-range-row">
            <div className="fp-range-header">
                <span className="fp-range-label">{label}</span>
                <span className="fp-range-value">{format(valueMin)} — {format(valueMax)}</span>
            </div>
            <Slider
                value={[valueMin, valueMax]}
                onChange={(_, v) => onChange(v[0], v[1])}
                min={min}
                max={max}
                valueLabelDisplay="auto"
                valueLabelFormat={format}
                size="small"
                sx={{
                    color: '#1a237e',
                    '& .MuiSlider-thumb': { width: 14, height: 14 },
                    '& .MuiSlider-valueLabel': {
                        fontFamily: 'Vazirmatn, sans-serif',
                        fontSize: '0.7rem',
                        background: '#1a237e',
                    },
                }}
            />
            <div className="fp-range-minmax">
                <span>{format(min)}</span>
                <span>{format(max)}</span>
            </div>
        </div>
    );
}

const CONVERSION = 33; // 33M deposit ≈ 1M rent/month

// ── Quick amount helpers ─────────────────────────────────────────────────────
const M = 1_000_000;
const B = 1_000_000_000;

function fmtAmount(n) {
    if (!n) return '';
    if (n >= B) return `${(n / B).toFixed(1)} م`;
    if (n >= M) return `${(n / M).toFixed(0)} م`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)} ه`;
    return String(n);
}

// Quick-pick button row — e.g. 100م, 200م, 500م, 1م‌م
function QuickPicks({ picks, onPick, current }) {
    return (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 3 }}>
            {picks.map(({ label, value }) => (
                <button
                    key={label}
                    type="button"
                    onClick={() => onPick(value)}
                    style={{
                        fontFamily: 'Vazirmatn, sans-serif',
                        fontSize: '0.65rem',
                        padding: '2px 6px',
                        borderRadius: 4,
                        border: `1px solid ${current === value ? '#1a237e' : '#d1d5db'}`,
                        background: current === value ? '#e8eaf6' : '#fff',
                        color: current === value ? '#1a237e' : '#6b7280',
                        cursor: 'pointer',
                        lineHeight: 1.4,
                    }}
                >{label}</button>
            ))}
        </div>
    );
}

// ── Rent budget integrated box ───────────────────────────────────────────────
function RentBudgetBox({ filters, onSubmit }) {
    const [local, setLocal] = useState({
        idealDeposit: filters.idealDeposit || '',
        idealRent: filters.idealRent || '',
        depositBudgetMax: filters.depositBudgetMax || '',
        rentBudgetMax: filters.rentBudgetMax || '',
    });

    useEffect(() => {
        setLocal({
            idealDeposit: filters.idealDeposit || '',
            idealRent: filters.idealRent || '',
            depositBudgetMax: filters.depositBudgetMax || '',
            rentBudgetMax: filters.rentBudgetMax || '',
        });
    }, [filters.idealDeposit, filters.idealRent, filters.depositBudgetMax, filters.rentBudgetMax]);

    const set = (key, val) => setLocal(prev => ({ ...prev, [key]: val }));

    const totalBudget = (local.idealDeposit && local.idealRent)
        ? Number(local.idealDeposit) + Number(local.idealRent) * CONVERSION
        : null;

    const rentIfMaxDeposit = (local.depositBudgetMax && totalBudget)
        ? Math.max(0, Math.round((totalBudget - Number(local.depositBudgetMax)) / CONVERSION))
        : null;

    const depositIfMaxRent = (local.rentBudgetMax && totalBudget)
        ? Math.max(0, totalBudget - Number(local.rentBudgetMax) * CONVERSION)
        : null;

    const handleSubmit = () => {
        onSubmit({
            idealDeposit: Number(local.idealDeposit) || 0,
            idealRent: Number(local.idealRent) || 0,
            depositBudgetMax: Number(local.depositBudgetMax) || 0,
            rentBudgetMax: Number(local.rentBudgetMax) || 0,
            totalBudget: totalBudget || 0,
        });
    };

    const inp = {
        fontFamily: 'Vazirmatn, sans-serif',
        fontSize: '0.78rem',
        direction: 'rtl',
        width: '100%',
        border: '1px solid #d1d5db',
        borderRadius: 6,
        padding: '5px 8px',
        outline: 'none',
        boxSizing: 'border-box',
    };
    const lbl = { fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.75rem', color: '#374151', marginBottom: 2, display: 'block' };
    const hint = { fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.7rem', color: '#6b7280', marginTop: 2, display: 'block' };
    const equiv = { fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.68rem', color: '#4f46e5', marginTop: 2, display: 'block' };

    const depositPicks = [
        { label: '۵۰م', value: 50 * M }, { label: '۱۰۰م', value: 100 * M },
        { label: '۲۰۰م', value: 200 * M }, { label: '۵۰۰م', value: 500 * M },
        { label: '۱م‌م', value: B },
    ];
    const rentPicks = [
        { label: '۵م', value: 5 * M }, { label: '۱۰م', value: 10 * M },
        { label: '۲۰م', value: 20 * M }, { label: '۵۰م', value: 50 * M },
    ];

    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', background: '#f9fafb' }}>
            <div style={{ fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.78rem', fontWeight: 600, color: '#1a237e', marginBottom: 8 }}>
                بودجه رهن و اجاره
            </div>

            {/* Ideal row */}
            <div style={{ fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.7rem', color: '#6b7280', marginBottom: 6 }}>
                ✦ مقادیر ایده‌آل (راحت‌ترین حالت)
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                    <label style={lbl}>ودیعه ایده‌آل (تومان)</label>
                    <input type="number" min="0" placeholder="مقدار" style={inp}
                        value={local.idealDeposit}
                        onChange={e => set('idealDeposit', e.target.value === '' ? '' : Number(e.target.value))}
                    />
                    {local.idealDeposit ? <span style={hint}>{fmtAmount(local.idealDeposit)} تومان</span> : null}
                    <QuickPicks picks={depositPicks} current={local.idealDeposit} onPick={v => set('idealDeposit', v)} />
                </div>
                <div style={{ flex: 1 }}>
                    <label style={lbl}>اجاره ایده‌آل (تومان/ماه)</label>
                    <input type="number" min="0" placeholder="مقدار" style={inp}
                        value={local.idealRent}
                        onChange={e => set('idealRent', e.target.value === '' ? '' : Number(e.target.value))}
                    />
                    {local.idealRent ? <span style={hint}>{fmtAmount(local.idealRent)} تومان</span> : null}
                    <QuickPicks picks={rentPicks} current={local.idealRent} onPick={v => set('idealRent', v)} />
                </div>
            </div>

            {/* Total budget pill */}
            {totalBudget && (
                <div style={{ background: '#e8eaf6', borderRadius: 6, padding: '5px 10px', marginBottom: 8, fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.72rem', color: '#1a237e', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span>بودجه کل معادل:</span>
                    <strong>{fmtAmount(totalBudget)} تومان</strong>
                    <span style={{ color: '#6b7280' }}>(آگهی‌هایی نشان داده می‌شوند که ودیعه + اجاره×۳۳ از این مقدار کمتر باشد)</span>
                </div>
            )}

            {/* Max / stretch row */}
            <div style={{ fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.7rem', color: '#6b7280', marginBottom: 6 }}>
                ✦ سقف حداکثر (منعطف)
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                    <label style={lbl}>حداکثر ودیعه (تومان)</label>
                    <input type="number" min="0" placeholder="مقدار" style={inp}
                        value={local.depositBudgetMax}
                        onChange={e => set('depositBudgetMax', e.target.value === '' ? '' : Number(e.target.value))}
                    />
                    {local.depositBudgetMax ? <span style={hint}>{fmtAmount(local.depositBudgetMax)} تومان</span> : null}
                    {rentIfMaxDeposit !== null && <span style={equiv}>← اجاره معادل: {fmtAmount(rentIfMaxDeposit)} ت/ماه</span>}
                    <QuickPicks picks={depositPicks} current={local.depositBudgetMax} onPick={v => set('depositBudgetMax', v)} />
                </div>
                <div style={{ flex: 1 }}>
                    <label style={lbl}>حداکثر اجاره (تومان/ماه)</label>
                    <input type="number" min="0" placeholder="مقدار" style={inp}
                        value={local.rentBudgetMax}
                        onChange={e => set('rentBudgetMax', e.target.value === '' ? '' : Number(e.target.value))}
                    />
                    {local.rentBudgetMax ? <span style={hint}>{fmtAmount(local.rentBudgetMax)} تومان</span> : null}
                    {depositIfMaxRent !== null && <span style={equiv}>← ودیعه معادل: {fmtAmount(depositIfMaxRent)} تومان</span>}
                    <QuickPicks picks={rentPicks} current={local.rentBudgetMax} onPick={v => set('rentBudgetMax', v)} />
                </div>
            </div>

            {/* Submit */}
            <button
                type="button"
                onClick={handleSubmit}
                style={{
                    width: '100%',
                    fontFamily: 'Vazirmatn, sans-serif',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    padding: '7px 0',
                    borderRadius: 6,
                    border: 'none',
                    background: '#1a237e',
                    color: '#fff',
                    cursor: 'pointer',
                }}
            >
                اعمال فیلتر بودجه
            </button>
        </div>
    );
}

// ── Sale price box ───────────────────────────────────────────────────────────
function SalePriceBox({ listingType, maxPrice, onSubmit }) {
    const [local, setLocal] = useState(maxPrice || '');

    useEffect(() => setLocal(maxPrice || ''), [maxPrice]);

    const salePicks = [
        { label: '۵۰۰م', value: 500 * M }, { label: '۱م‌م', value: B },
        { label: '۲م‌م', value: 2 * B }, { label: '۵م‌م', value: 5 * B },
        { label: '۱۰م‌م', value: 10 * B },
    ];

    const lbl = listingType === 'auction' ? 'سقف قیمت مزایده (تومان)'
        : listingType === 'presale' ? 'سقف قیمت پیش‌فروش (تومان)'
            : 'سقف قیمت فروش (تومان)';

    const inp = {
        fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.78rem', direction: 'rtl',
        width: '100%', border: '1px solid #d1d5db', borderRadius: 6,
        padding: '5px 8px', outline: 'none', boxSizing: 'border-box',
    };

    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', background: '#f9fafb' }}>
            <label style={{ fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.75rem', color: '#374151', marginBottom: 4, display: 'block' }}>{lbl}</label>
            <input type="number" min="0" placeholder="مقدار" style={inp}
                value={local}
                onChange={e => setLocal(e.target.value === '' ? '' : Number(e.target.value))}
            />
            {local ? <span style={{ fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.7rem', color: '#6b7280', marginTop: 2, display: 'block' }}>{fmtAmount(local)} تومان</span> : null}
            <QuickPicks picks={salePicks} current={local} onPick={v => setLocal(v)} />
            <button type="button" onClick={() => onSubmit(Number(local) || 0)}
                style={{ marginTop: 8, width: '100%', fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.8rem', fontWeight: 600, padding: '7px 0', borderRadius: 6, border: 'none', background: '#1a237e', color: '#fff', cursor: 'pointer' }}>
                اعمال فیلتر قیمت
            </button>
        </div>
    );
}

// ── Build a human-readable list of active filter chips ──────────────────────
function buildActiveChips(filters, propertyTypes, usageTypes, amenitiesList) {
    const chips = [];
    const fmt2 = (n) => {
        if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} م`;
        if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)} م`;
        if (n >= 1_000) return `${(n / 1_000).toFixed(0)} ه`;
        return String(n);
    };

    const listingLabels = { rent: 'رهن و اجاره', sale: 'خرید و فروش', auction: 'مزایده', presale: 'پیش‌فروش' };
    if (filters.listingType && filters.listingType !== 'all')
        chips.push({ key: 'listingType', label: listingLabels[filters.listingType] || filters.listingType });

    if (filters.propertyType && filters.propertyType !== 'all') {
        const ids = Array.isArray(filters.propertyType) ? filters.propertyType : [filters.propertyType];
        const labels = ids.map(id => propertyTypes.find(x => x.id === id)?.name || id);
        chips.push({ key: 'propertyType', label: `ملک: ${labels.join('، ')}` });
    }
    if (filters.usageType && filters.usageType !== 'all') {
        const ids = Array.isArray(filters.usageType) ? filters.usageType : [filters.usageType];
        const labels = ids.map(id => usageTypes.find(x => x.id === id)?.name || id);
        chips.push({ key: 'usageType', label: `کاربری: ${labels.join('، ')}` });
    }
    if (filters.minArea > 0 || (filters.maxArea && filters.maxArea < 100000))
        chips.push({ key: 'area', label: `متراژ: ${filters.minArea || 0}–${filters.maxArea} م²` });

    if (filters.minPrice > 0 || (filters.maxPrice && filters.maxPrice < 100000000000))
        chips.push({ key: 'price', label: `قیمت: تا ${fmt2(filters.maxPrice)}` });
    if (filters.idealDeposit > 0 || filters.idealRent > 0 || filters.depositBudgetMax > 0 || filters.rentBudgetMax > 0)
        chips.push({ key: 'rentBudget', label: `بودجه اجاره` });

    if (filters.geoFilters && filters.geoFilters.length > 0) {
        const typeLabels = { city: 'شهر', region: 'منطقه', neighborhood: 'محله' };
        filters.geoFilters.forEach(gf => {
            chips.push({
                key: `geoFilter_${gf.type}_${gf.id}`,
                label: `${typeLabels[gf.type] || gf.type}: ${gf.name}`,
                _geoFilterItem: gf,
            });
        });
    }
    if (filters.aiCandidateHexIds?.length > 0) {
        chips.push({
            key: 'aiSpatial',
            label: `محدوده هوشمند (${filters.aiCandidateHexIds.length} سلول)`,
        });
    }

    const amenityCount = Object.keys(filters.amenities || {}).length;
    if (amenityCount > 0)
        chips.push({ key: 'amenities', label: `امکانات (${amenityCount})` });

    const geoCount = Object.keys(filters.geoFeatures || {}).length;
    if (geoCount > 0)
        chips.push({ key: 'geoFeatures', label: `جغرافیایی (${geoCount})` });

    return chips;
}

// ── Geo feature: boolean pill ────────────────────────────────────────────────
function GeoFeatureBooleanPill({ feature, value, onChange }) {
    const active = !!value;
    return (
        <button
            type="button"
            className={`fp-pill${active ? ' fp-pill--on' : ''}`}
            onClick={() => onChange(!active)}
            aria-pressed={active}
        >
            {active && <span className="fp-pill-check">✓</span>}
            {feature.description || feature.name}
        </button>
    );
}

// street_width bucket definitions
const STREET_WIDTH_BUCKETS = [
    { label: '< ۱۲ متر', id: 'lt12' },
    { label: '۱۲ – ۲۴ متر', id: '12_24' },
    { label: '۲۴ – ۴۰ متر', id: '24_40' },
    { label: '> ۴۰ متر', id: 'gt40' },
];

/** Returns true if a numeric street_width value falls inside the bucket */
export function matchStreetWidthBucket(numericValue, bucketId) {
    const v = parseFloat(numericValue);
    if (isNaN(v)) return false;
    if (bucketId === 'lt12') return v < 12;
    if (bucketId === '12_24') return v >= 12 && v < 24;
    if (bucketId === '24_40') return v >= 24 && v < 40;
    if (bucketId === 'gt40') return v >= 40;
    return false;
}

// ── Geo feature: searchable multi-select dropdown ───────────────────────────
function GeoFeatureCategorical({ feature, selected = [], options, onChange }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = React.useRef(null);

    const filtered = options.filter(o =>
        o.toLowerCase().includes(search.toLowerCase())
    );
    const toggle = (opt) => {
        const next = selected.includes(opt)
            ? selected.filter(v => v !== opt)
            : [...selected, opt];
        onChange(next);
    };

    // Close dropdown on outside click
    React.useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const triggerLabel = selected.length === 0
        ? 'انتخاب کنید...'
        : selected.length === 1
            ? selected[0]
            : `${selected.length} مورد انتخاب شده`;

    return (
        <div ref={ref} style={{ marginBottom: 6, position: 'relative' }}>
            {/* Label row */}
            <div style={{ fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.75rem', fontWeight: 600, color: '#1a237e', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                {feature.description || feature.name}
                {selected.length > 0 && (
                    <span style={{ background: '#1a237e', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: '0.65rem' }}>
                        {selected.length}
                    </span>
                )}
            </div>

            {/* Trigger button */}
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.75rem', direction: 'rtl',
                    border: `1px solid ${open ? '#1a237e' : '#d1d5db'}`, borderRadius: 6,
                    padding: '5px 8px', background: '#fff', cursor: 'pointer', color: selected.length ? '#111827' : '#9ca3af',
                    boxSizing: 'border-box',
                }}
            >
                <span>{triggerLabel}</span>
                <span style={{ fontSize: '0.6rem', color: '#6b7280', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▼</span>
            </button>

            {/* Dropdown panel */}
            {open && (
                <div style={{
                    position: 'absolute', top: '100%', right: 0, left: 0, zIndex: 200,
                    background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', marginTop: 2,
                }}>
                    {/* Search */}
                    <div style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>
                        <input
                            type="text"
                            placeholder="جستجو..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            autoFocus
                            style={{
                                width: '100%', boxSizing: 'border-box',
                                fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.75rem',
                                direction: 'rtl', border: '1px solid #e5e7eb', borderRadius: 4,
                                padding: '4px 8px', outline: 'none', background: '#f9fafb',
                            }}
                        />
                    </div>

                    {/* Options list */}
                    <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                        {filtered.length === 0 && (
                            <div style={{ fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.72rem', color: '#9ca3af', padding: '8px 10px', textAlign: 'right' }}>
                                موردی یافت نشد
                            </div>
                        )}
                        {filtered.map(opt => {
                            const on = selected.includes(opt);
                            return (
                                <div
                                    key={opt}
                                    onClick={() => toggle(opt)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 6,
                                        padding: '6px 10px', cursor: 'pointer', direction: 'rtl',
                                        background: on ? '#e8eaf6' : 'transparent',
                                        fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.75rem',
                                        color: on ? '#1a237e' : '#111827',
                                    }}
                                    onMouseEnter={e => { if (!on) e.currentTarget.style.background = '#f3f4f6'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = on ? '#e8eaf6' : 'transparent'; }}
                                >
                                    <span style={{
                                        width: 14, height: 14, flexShrink: 0, border: `1.5px solid ${on ? '#1a237e' : '#d1d5db'}`,
                                        borderRadius: 3, background: on ? '#1a237e' : '#fff',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: '#fff',
                                    }}>{on ? '✓' : ''}</span>
                                    {opt}
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer */}
                    {selected.length > 0 && (
                        <div style={{ borderTop: '1px solid #f3f4f6', padding: '5px 8px', display: 'flex', justifyContent: 'flex-end' }}>
                            <button type="button" onClick={() => { onChange([]); setOpen(false); }}
                                style={{ fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.68rem', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                × پاک کردن انتخاب
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Selected chips preview */}
            {selected.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>
                    {selected.map(opt => (
                        <span key={opt} style={{
                            fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.65rem',
                            background: '#e8eaf6', color: '#1a237e', borderRadius: 10,
                            padding: '2px 6px', display: 'flex', alignItems: 'center', gap: 3,
                        }}>
                            {opt}
                            <button type="button" onClick={() => toggle(opt)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#5c6bc0', fontSize: '0.7rem', lineHeight: 1 }}>×</button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Geo feature: street-width bucket multi-select ───────────────────────────
function GeoFeatureStreetWidth({ feature, selected = [], onChange }) {
    const toggle = (id) => {
        const next = selected.includes(id)
            ? selected.filter(v => v !== id)
            : [...selected, id];
        onChange(next);
    };
    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', background: '#f9fafb', marginBottom: 6 }}>
            <div style={{ fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.75rem', fontWeight: 600, color: '#1a237e', marginBottom: 6 }}>
                {feature.description || feature.name}
                {selected.length > 0 && (
                    <span style={{ marginRight: 6, background: '#1a237e', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: '0.65rem' }}>
                        {selected.length}
                    </span>
                )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {STREET_WIDTH_BUCKETS.map(({ label, id }) => {
                    const on = selected.includes(id);
                    return (
                        <button key={id} type="button"
                            className={`fp-seg-btn${on ? ' fp-seg-btn--on' : ''}`}
                            onClick={() => toggle(id)}
                            aria-pressed={on}
                            style={{ fontSize: '0.72rem' }}
                        >
                            {on && <span className="fp-pill-check">✓ </span>}
                            {label}
                        </button>
                    );
                })}
            </div>
            {selected.length > 0 && (
                <button type="button"
                    onClick={() => onChange([])}
                    style={{ marginTop: 6, fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.68rem', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                    × پاک کردن انتخاب
                </button>
            )}
        </div>
    );
}

// ── Default values for clearing individual chips ─────────────────────────────
const CHIP_CLEAR = {
    listingType: { listingType: 'all' },
    propertyType: { propertyType: 'all' },
    usageType: { usageType: 'all' },
    area: { minArea: 0, maxArea: 0 },
    price: { minPrice: 0, maxPrice: 0 },
    rentBudget: { idealDeposit: 0, idealRent: 0, depositBudgetMax: 0, rentBudgetMax: 0, totalBudget: 0 },
    deposit: { minDeposit: 0, maxDeposit: 100000000000 },
    rent: { minRent: 0, maxRent: 100000000000 },
    geoFilters: { geoFilters: [] },
    amenities: { amenities: {} },
    geoFeatures: { geoFeatures: {} },
    aiSpatial: { aiCandidateHexIds: [], geoFilters: [] },
};

// ── Main component ───────────────────────────────────────────────────────────
const FilterPanel = ({
    filters,
    onFilterChange,
    onAmenitiesDataChange,
    onClearFilters,
    onLookupsChange,
    sortPriorities = [],
    onSortPrioritiesChange,
}) => {
    const [propertyTypes, setPropertyTypes] = useState([]);
    const [usageTypes, setUsageTypes] = useState([]);
    const [amenities, setAmenities] = useState([]);
    const [geoFeatures, setGeoFeatures] = useState([]);
    const [zoningOptions, setZoningOptions] = useState({ zone_code: null, zone_desc: null });
    const [lookupError, setLookupError] = useState(false);

    useEffect(() => { fetchFilterData(); }, []);

    const authHeaders = () => {
        const token = localStorage.getItem('access_token');
        return { Authorization: `Bearer ${token}` };
    };

    const fetchFilterData = async () => {
        try {
            setLookupError(false);
            if (filterLookupCache) {
                const { propertyTypes: pts, usageTypes: uts, amenities: ams, geoFeatures: gfs } = filterLookupCache;
                setPropertyTypes(pts);
                setUsageTypes(uts);
                setAmenities(ams);
                setGeoFeatures(gfs);
                if (filterLookupCache.zoningOptions) setZoningOptions(filterLookupCache.zoningOptions);
                onAmenitiesDataChange?.(ams);
                onLookupsChange?.({ propertyTypes: pts, usageTypes: uts, geoFeatures: gfs });
                return;
            }
            const h = authHeaders();
            const [ptRes, utRes, amRes, gfRes] = await Promise.all([
                api.get(apiRoutes.properties.propertyTypes, { headers: h }),
                api.get(apiRoutes.properties.usages, { headers: h }),
                api.get(apiRoutes.properties.amenities, { headers: h }),
                api.get(apiRoutes.geo.features, { headers: h }),
            ]);
            setPropertyTypes(ptRes.data);
            setUsageTypes(utRes.data);
            setAmenities(amRes.data);
            setGeoFeatures(gfRes.data);
            filterLookupCache = {
                propertyTypes: ptRes.data,
                usageTypes: utRes.data,
                amenities: amRes.data,
                geoFeatures: gfRes.data,
            };
            if (onAmenitiesDataChange) onAmenitiesDataChange(amRes.data);
            if (onLookupsChange) onLookupsChange({
                propertyTypes: ptRes.data,
                usageTypes: utRes.data,
                geoFeatures: gfRes.data,
            });

            // Fetch zoning options if relevant categorical features exist
            const catFeatures = gfRes.data.filter(f => f.feature_type === 'categorical');
            const needsZoneCode = catFeatures.some(f => f.name === 'zonning');
            const needsZoneDesc = catFeatures.some(f => f.name === 'zone_desc');
            const zoningUpdates = {};
            if (needsZoneCode) {
                try {
                    const r = await api.get(apiRoutes.geo.zoningOptions, { params: { field: 'zone_code' }, headers: h });
                    zoningUpdates.zone_code = r.data;
                } catch (e) {  }
            }
            if (needsZoneDesc) {
                try {
                    const r = await api.get(apiRoutes.geo.zoningOptions, { params: { field: 'zone_desc' }, headers: h });
                    zoningUpdates.zone_desc = r.data;
                } catch (e) {  }
            }
            if (Object.keys(zoningUpdates).length > 0) {
                setZoningOptions(prev => ({ ...prev, ...zoningUpdates }));
                filterLookupCache = {
                    ...filterLookupCache,
                    zoningOptions: { zone_code: null, zone_desc: null, ...zoningUpdates },
                };
            }
        } catch (e) {

            setLookupError(true);
        }
    };

    const handleAmenityChange = (amenityId, value, valueType) => {
        const cur = filters.amenities || {};
        if (valueType === 'boolean') {
            if (value) {
                onFilterChange('amenities', { ...cur, [amenityId]: value });
            } else {
                const next = { ...cur };
                delete next[amenityId];
                onFilterChange('amenities', next);
            }
        } else if (valueType === 'categorical') {
            if (value === '' || value === null || value === undefined) {
                // "همه" — clear selection
                const next = { ...cur };
                delete next[amenityId];
                onFilterChange('amenities', next);
            } else {
                // Toggle value in/out of the selected array
                const current = Array.isArray(cur[amenityId]) ? cur[amenityId] : [];
                const already = current.includes(value);
                const next = already
                    ? current.filter(v => v !== value)
                    : [...current, value];
                if (next.length === 0) {
                    const cleared = { ...cur };
                    delete cleared[amenityId];
                    onFilterChange('amenities', cleared);
                } else {
                    onFilterChange('amenities', { ...cur, [amenityId]: next });
                }
            }
        } else {
            if (value !== null && value !== undefined && value !== '') {
                onFilterChange('amenities', { ...cur, [amenityId]: value });
            } else {
                const next = { ...cur };
                delete next[amenityId];
                onFilterChange('amenities', next);
            }
        }
    };

    const handleGeoFeatureChange = (featureId, value) => {
        onFilterChange('geoFeatures', { ...(filters.geoFeatures || {}), [featureId]: value });
    };

    // Active-only, sorted by priority, then split by filter_usage
    const activeAmenities = [...amenities]
        .filter(a => a.is_active !== false)
        .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));
    const activeGeoFeatures = [...geoFeatures]
        .filter(f => f.is_active !== false)
        .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));

    const filteringAmenities = activeAmenities.filter(a => a.filter_usage !== 'sorting');
    const sortingAmenities = activeAmenities.filter(a => a.filter_usage === 'sorting');
    const filteringGeoFeatures = activeGeoFeatures.filter(f => f.filter_usage === 'filtering' && f.showable_in_filter_pane === true);
    const sortingGeoFeatures = activeGeoFeatures.filter(f => f.filter_usage === 'sorting' && f.showable_in_filter_pane === true);

    // Build chips from current filter state
    const activeChips = buildActiveChips(filters, propertyTypes, usageTypes, amenities);

    const handleRemoveChip = (chipKey, chip) => {
        if (chipKey.startsWith('geoFilter_') && chip?._geoFilterItem) {
            // Remove just this one geo filter item
            const item = chip._geoFilterItem;
            const next = (filters.geoFilters || []).filter(
                f => !(f.type === item.type && f.id === item.id)
            );
            onFilterChange('geoFilters', next);
            return;
        }
        const resets = CHIP_CLEAR[chipKey] || {};
        Object.entries(resets).forEach(([k, v]) => onFilterChange(k, v));
    };

    return (
        <div className="filter-panel">
            {/* ── Header ── */}
            <div className="fp-header">
                <TuneIcon sx={{ fontSize: 18, color: '#1a237e' }} />
                <span className="fp-header-title">فیلترهای جستجو</span>
            </div>

            {lookupError && (
                <div className="fp-load-error" role="alert">
                    <span>فیلترها بارگذاری نشدند.</span>
                    <button type="button" onClick={fetchFilterData}>تلاش دوباره</button>
                </div>
            )}

            {/* ── Active filters summary ── */}
            {activeChips.length > 0 && (
                <div className="fp-active-filters">
                    <div className="fp-active-filters-header">
                        <span className="fp-active-filters-title">فیلترهای فعال</span>
                        <button className="fp-clear-all" onClick={onClearFilters}>پاک کردن همه</button>
                    </div>
                    <div className="fp-chips-wrap">
                        {activeChips.map((chip) => (
                            <span key={chip.key} className="fp-chip">
                                {chip.label}
                                <button
                                    className="fp-chip-remove"
                                    onClick={() => handleRemoveChip(chip.key, chip)}
                                    aria-label={`حذف ${chip.label}`}
                                >×</button>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <div className="fp-body">
                <Grid container spacing={1.5}>

                    {/* Listing type */}
                    <Grid item xs={12}>
                        <div className="fp-seg-wrap">
                            <div className="fp-seg-label">نوع آگهی</div>
                            <div className="fp-seg" style={{ flexWrap: 'wrap' }}>
                                {[
                                    { value: 'all', label: 'همه' },
                                    { value: 'rent', label: 'رهن و اجاره' },
                                    { value: 'sale', label: 'خرید و فروش' },
                                    { value: 'auction', label: 'مزایده' },
                                    { value: 'presale', label: 'پیش‌فروش' },
                                ].map(({ value, label }) => (
                                    <button
                                        key={value}
                                        type="button"
                                        className={`fp-seg-btn${(filters.listingType || 'all') === value ? ' fp-seg-btn--on' : ''}`}
                                        onClick={() => onFilterChange('listingType', value)}
                                    >{label}</button>
                                ))}
                            </div>
                        </div>
                    </Grid>

                    {/* Property type */}
                    <Grid item xs={12}>
                        <div className="fp-seg-wrap">
                            <div className="fp-seg-label">نوع ملک</div>
                            <div className="fp-seg" style={{ flexWrap: 'wrap' }}>
                                <button
                                    type="button"
                                    className={`fp-seg-btn${(filters.propertyType || 'all') === 'all' ? ' fp-seg-btn--on' : ''}`}
                                    onClick={() => onFilterChange('propertyType', 'all')}
                                >همه</button>
                                {propertyTypes.map((t) => (
                                    <button
                                        key={t.id}
                                        type="button"
                                        className={`fp-seg-btn${selectionContains(filters.propertyType, t.id) ? ' fp-seg-btn--on' : ''}`}
                                        onClick={() => onFilterChange('propertyType', t.id)}
                                    >{t.name}</button>
                                ))}
                            </div>
                        </div>
                    </Grid>

                    {/* Usage type */}
                    <Grid item xs={12}>
                        <div className="fp-seg-wrap">
                            <div className="fp-seg-label">نوع کاربری</div>
                            <div className="fp-seg" style={{ flexWrap: 'wrap' }}>
                                <button
                                    type="button"
                                    className={`fp-seg-btn${(filters.usageType || 'all') === 'all' ? ' fp-seg-btn--on' : ''}`}
                                    onClick={() => onFilterChange('usageType', 'all')}
                                >همه</button>
                                {usageTypes.map((t) => (
                                    <button
                                        key={t.id}
                                        type="button"
                                        className={`fp-seg-btn${selectionContains(filters.usageType, t.id) ? ' fp-seg-btn--on' : ''}`}
                                        onClick={() => onFilterChange('usageType', t.id)}
                                    >{t.name}</button>
                                ))}
                            </div>
                        </div>
                    </Grid>

                    {/* Area */}
                    <Grid item xs={12}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontFamily: 'Vazirmatn, sans-serif', fontSize: '1rem', color: '#050505ff', whiteSpace: 'nowrap', flexShrink: 0 }}>متراژ (م²)</span>
                            <TextField
                                size="small"
                                placeholder="از"
                                type="number"
                                inputProps={{ min: 0, style: { padding: '4px 8px', fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.78rem' } }}
                                value={filters.minArea || ''}
                                onChange={(e) => onFilterChange('minArea', e.target.value === '' ? 0 : Number(e.target.value))}
                                sx={{ width: 80, '& .MuiOutlinedInput-root': { height: 30 } }}
                            />
                            <span style={{ fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.75rem', color: '#9ca3af' }}>—</span>
                            <TextField
                                size="small"
                                placeholder="تا"
                                type="number"
                                inputProps={{ min: 0, style: { padding: '4px 8px', fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.78rem' } }}
                                value={filters.maxArea || ''}
                                onChange={(e) => onFilterChange('maxArea', e.target.value === '' ? 0 : Number(e.target.value))}
                                sx={{ width: 80, '& .MuiOutlinedInput-root': { height: 30 } }}
                            />
                        </div>
                    </Grid>

                    {/* Rent budget box */}
                    {filters.listingType === 'rent' && (
                        <Grid item xs={12}>
                            <RentBudgetBox
                                filters={filters}
                                onSubmit={(vals) => {
                                    Object.entries(vals).forEach(([k, v]) => onFilterChange(k, v));
                                }}
                            />
                        </Grid>
                    )}

                    {/* Sale / Auction / Presale price */}
                    {(filters.listingType === 'sale' || filters.listingType === 'auction' || filters.listingType === 'presale') && (
                        <Grid item xs={12}>
                            <SalePriceBox listingType={filters.listingType} maxPrice={filters.maxPrice} onSubmit={(v) => onFilterChange('maxPrice', v)} />
                        </Grid>
                    )}

                    {/* ── Amenities accordion ── */}
                    <Grid item xs={12}>
                        <Accordion className="fp-accordion" disableGutters>
                            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />} className="fp-accordion-summary">
                                <span className="fp-accordion-title">امکانات</span>
                                {Object.keys(filters.amenities || {}).length > 0 && (
                                    <Chip
                                        label={Object.keys(filters.amenities).length}
                                        size="small"
                                        className="fp-accordion-badge"
                                    />
                                )}
                            </AccordionSummary>
                            <AccordionDetails className="fp-accordion-details">

                                {/* ── FILTERING amenities ── */}
                                {filteringAmenities.length > 0 && (
                                    <>
                                        <div className="fp-usage-section-label fp-usage-filter">
                                            <span className="fp-usage-icon">🔍</span> فیلتر کردن
                                            <span className="fp-usage-hint">ملک‌های غیر منطبق مخفی می‌شوند</span>
                                        </div>

                                        {/* Boolean */}
                                        {filteringAmenities.filter(a => a.value_type === 'boolean').length > 0 && (
                                            <div className="fp-amenity-group">
                                                <div className="fp-pill-grid">
                                                    {filteringAmenities.filter(a => a.value_type === 'boolean').map((a) => {
                                                        const active = !!(filters.amenities || {})[a.id];
                                                        return (
                                                            <button
                                                                key={a.id}
                                                                type="button"
                                                                className={`fp-pill${active ? ' fp-pill--on' : ''}`}
                                                                onClick={() => handleAmenityChange(a.id, !active, 'boolean')}
                                                                aria-pressed={active}
                                                            >
                                                                {active && <span className="fp-pill-check">✓</span>}
                                                                {a.description || a.name}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Categorical */}
                                        {filteringAmenities.filter(a => a.value_type === 'categorical').length > 0 && (
                                            <div className="fp-amenity-group">
                                                {filteringAmenities.filter(a => a.value_type === 'categorical').map((a) => {
                                                    const selected = (filters.amenities || {})[a.id];
                                                    const selectedArr = Array.isArray(selected) ? selected : [];
                                                    const options = a.categorical_options ? Object.entries(a.categorical_options) : [];
                                                    return (
                                                        <div key={a.id} className="fp-seg-wrap">
                                                            <div className="fp-seg-label">{a.description || a.name}</div>
                                                            <div className="fp-seg" style={{ flexWrap: 'wrap' }}>
                                                                <button type="button"
                                                                    className={`fp-seg-btn${selectedArr.length === 0 ? ' fp-seg-btn--on' : ''}`}
                                                                    onClick={() => handleAmenityChange(a.id, '', 'categorical')}
                                                                >همه</button>
                                                                {options.map(([k, v]) => {
                                                                    const numKey = Number(k);
                                                                    const isOn = selectedArr.includes(numKey);
                                                                    return (
                                                                        <button key={k} type="button"
                                                                            className={`fp-seg-btn${isOn ? ' fp-seg-btn--on' : ''}`}
                                                                            onClick={() => handleAmenityChange(a.id, numKey, 'categorical')}
                                                                            aria-pressed={isOn}
                                                                        >
                                                                            {isOn && <span className="fp-pill-check">✓ </span>}
                                                                            {v}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Numerical - compact inline sliders */}
                                        {filteringAmenities.filter(a => a.value_type === 'numerical').length > 0 && (
                                            <div className="fp-numerical-compact">
                                                {filteringAmenities.filter(a => a.value_type === 'numerical').map((a) => {
                                                    const val = (filters.amenities || {})[a.id] || [a.min_value || 0, a.max_value || 100];
                                                    const min = a.min_value || 0;
                                                    const max = a.max_value || 100;
                                                    return (
                                                        <div key={a.id} className="fp-num-row">
                                                            <div className="fp-num-header">
                                                                <span className="fp-num-label">{a.description || a.name}</span>
                                                                <span className="fp-num-val">{val[0]} — {val[1]}</span>
                                                            </div>
                                                            <Slider
                                                                value={[val[0], val[1]]}
                                                                onChange={(_, v) => handleAmenityChange(a.id, v, 'numerical')}
                                                                min={min} max={max}
                                                                size="small"
                                                                sx={{
                                                                    color: '#5c6bc0',
                                                                    height: 4,
                                                                    padding: '4px 0',
                                                                    '& .MuiSlider-thumb': { width: 12, height: 12 },
                                                                    '& .MuiSlider-track': { height: 4 },
                                                                    '& .MuiSlider-rail': { height: 4, opacity: 0.3 },
                                                                }}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Text */}
                                        {filteringAmenities.filter(a => a.value_type === 'text').length > 0 && (
                                            <div className="fp-amenity-group">
                                                <div className="fp-sub-label">جستجوی متنی</div>
                                                {filteringAmenities.filter(a => a.value_type === 'text').map((a) => {
                                                    const val = (filters.amenities || {})[a.id] || '';
                                                    return (
                                                        <div key={a.id} className="fp-text-search">
                                                            <span className="fp-text-search-icon">🔍</span>
                                                            <input type="text" className="fp-text-search-input"
                                                                placeholder={a.description || a.name} value={val}
                                                                onChange={(e) => handleAmenityChange(a.id, e.target.value, 'text')}
                                                            />
                                                            {val && (
                                                                <button type="button" className="fp-text-search-clear"
                                                                    onClick={() => handleAmenityChange(a.id, '', 'text')}
                                                                    aria-label="پاک کردن">×</button>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* ── SORTING amenities ── */}
                                {sortingAmenities.length > 0 && (
                                    <>
                                        {filteringAmenities.length > 0 && <div className="fp-usage-divider" />}
                                        <div className="fp-usage-section-label fp-usage-sort">
                                            <span className="fp-usage-icon">↕</span> مرتب‌سازی
                                            <span className="fp-usage-hint">ترتیب نمایش و رنگ‌بندی نقشه را تعیین می‌کند</span>
                                        </div>

                                        {/* Boolean */}
                                        {sortingAmenities.filter(a => a.value_type === 'boolean').length > 0 && (
                                            <div className="fp-amenity-group">
                                                <div className="fp-pill-grid">
                                                    {sortingAmenities.filter(a => a.value_type === 'boolean').map((a) => {
                                                        const active = !!(filters.amenities || {})[a.id];
                                                        return (
                                                            <button key={a.id} type="button"
                                                                className={`fp-pill fp-pill--sort${active ? ' fp-pill--on' : ''}`}
                                                                onClick={() => handleAmenityChange(a.id, !active, 'boolean')}
                                                                aria-pressed={active}
                                                            >
                                                                {active && <span className="fp-pill-check">✓</span>}
                                                                {a.description || a.name}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Categorical */}
                                        {sortingAmenities.filter(a => a.value_type === 'categorical').length > 0 && (
                                            <div className="fp-amenity-group">
                                                {sortingAmenities.filter(a => a.value_type === 'categorical').map((a) => {
                                                    const selected = (filters.amenities || {})[a.id];
                                                    const selectedArr = Array.isArray(selected) ? selected : [];
                                                    const options = a.categorical_options ? Object.entries(a.categorical_options) : [];
                                                    return (
                                                        <div key={a.id} className="fp-seg-wrap">
                                                            <div className="fp-seg-label">{a.description || a.name}</div>
                                                            <div className="fp-seg" style={{ flexWrap: 'wrap' }}>
                                                                <button type="button"
                                                                    className={`fp-seg-btn${selectedArr.length === 0 ? ' fp-seg-btn--on' : ''}`}
                                                                    onClick={() => handleAmenityChange(a.id, '', 'categorical')}
                                                                >همه</button>
                                                                {options.map(([k, v]) => {
                                                                    const numKey = Number(k);
                                                                    const isOn = selectedArr.includes(numKey);
                                                                    return (
                                                                        <button key={k} type="button"
                                                                            className={`fp-seg-btn${isOn ? ' fp-seg-btn--on' : ''}`}
                                                                            onClick={() => handleAmenityChange(a.id, numKey, 'categorical')}
                                                                            aria-pressed={isOn}
                                                                        >
                                                                            {isOn && <span className="fp-pill-check">✓ </span>}
                                                                            {v}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Numerical - compact inline sliders */}
                                        {sortingAmenities.filter(a => a.value_type === 'numerical').length > 0 && (
                                            <div className="fp-numerical-compact fp-numerical-sort">
                                                {sortingAmenities.filter(a => a.value_type === 'numerical').map((a) => {
                                                    const val = (filters.amenities || {})[a.id] || [a.min_value || 0, a.max_value || 100];
                                                    const min = a.min_value || 0;
                                                    const max = a.max_value || 100;
                                                    return (
                                                        <div key={a.id} className="fp-num-row">
                                                            <div className="fp-num-header">
                                                                <span className="fp-num-label">{a.description || a.name}</span>
                                                                <span className="fp-num-val">{val[0]} — {val[1]}</span>
                                                            </div>
                                                            <Slider
                                                                value={[val[0], val[1]]}
                                                                onChange={(_, v) => handleAmenityChange(a.id, v, 'numerical')}
                                                                min={min} max={max}
                                                                size="small"
                                                                sx={{
                                                                    color: '#e65100',
                                                                    height: 4,
                                                                    padding: '4px 0',
                                                                    '& .MuiSlider-thumb': { width: 12, height: 12 },
                                                                    '& .MuiSlider-track': { height: 4 },
                                                                    '& .MuiSlider-rail': { height: 4, opacity: 0.3 },
                                                                }}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </>
                                )}

                            </AccordionDetails>
                        </Accordion>
                    </Grid>

                    {/* ── Geo features accordion ── */}
                    {geoFeatures.length > 0 && (
                        <Grid item xs={12}>
                            <Accordion className="fp-accordion" disableGutters>
                                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />} className="fp-accordion-summary">
                                    <span className="fp-accordion-title">ویژگی‌های جغرافیایی</span>
                                    {Object.keys(filters.geoFeatures || {}).length > 0 && (
                                        <Chip
                                            label={Object.keys(filters.geoFeatures).length}
                                            size="small"
                                            className="fp-accordion-badge"
                                        />
                                    )}
                                </AccordionSummary>
                                <AccordionDetails className="fp-accordion-details">

                                    {/* ── FILTERING geo features ── */}
                                    {filteringGeoFeatures.length > 0 && (
                                        <>
                                            <div className="fp-usage-section-label fp-usage-filter">
                                                <span className="fp-usage-icon">🔍</span> فیلتر کردن
                                                <span className="fp-usage-hint">ملک‌های غیر منطبق مخفی می‌شوند</span>
                                            </div>
                                            <div className="fp-amenity-group" style={{ marginTop: 0 }}>
                                                {filteringGeoFeatures.map((f) => {
                                                    const ft = f.feature_type;

                                                    // ── boolean ──
                                                    if (ft === 'boolian') {
                                                        return (
                                                            <div key={f.id} className="fp-pill-grid" style={{ marginBottom: 4 }}>
                                                                <GeoFeatureBooleanPill
                                                                    feature={f}
                                                                    value={(filters.geoFeatures || {})[f.id]}
                                                                    onChange={(v) => {
                                                                        if (v) handleGeoFeatureChange(f.id, true);
                                                                        else {
                                                                            const next = { ...(filters.geoFeatures || {}) };
                                                                            delete next[f.id];
                                                                            onFilterChange('geoFeatures', next);
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                        );
                                                    }

                                                    // ── categorical ──
                                                    if (ft === 'categorical') {
                                                        const selected = (filters.geoFeatures || {})[f.id] || [];

                                                        // street_width special case
                                                        if (f.name === 'street_width') {
                                                            return (
                                                                <GeoFeatureStreetWidth
                                                                    key={f.id}
                                                                    feature={f}
                                                                    selected={selected}
                                                                    onChange={(vals) => {
                                                                        if (vals.length === 0) {
                                                                            const next = { ...(filters.geoFeatures || {}) };
                                                                            delete next[f.id];
                                                                            onFilterChange('geoFeatures', next);
                                                                        } else {
                                                                            handleGeoFeatureChange(f.id, vals);
                                                                        }
                                                                    }}
                                                                />
                                                            );
                                                        }

                                                        // zonning / zone_desc from ZoningData
                                                        const opts =
                                                            f.name === 'zonning' ? (zoningOptions.zone_code || []) :
                                                                f.name === 'zone_desc' ? (zoningOptions.zone_desc || []) :
                                                                    [];

                                                        return (
                                                            <GeoFeatureCategorical
                                                                key={f.id}
                                                                feature={f}
                                                                selected={selected}
                                                                options={opts}
                                                                onChange={(vals) => {
                                                                    if (vals.length === 0) {
                                                                        const next = { ...(filters.geoFeatures || {}) };
                                                                        delete next[f.id];
                                                                        onFilterChange('geoFeatures', next);
                                                                    } else {
                                                                        handleGeoFeatureChange(f.id, vals);
                                                                    }
                                                                }}
                                                            />
                                                        );
                                                    }

                                                    // ── range slider (count, nearest_distance, score, numerical, custom) ──
                                                    return (
                                                        <RangeSlider key={f.id}
                                                            label={`${f.description || f.name}${f.unit ? ` (${f.unit})` : ''}`}
                                                            min={f.min_value || 0} max={f.max_value || 100}
                                                            valueMin={(filters.geoFeatures?.[f.id] || [f.min_value || 0, f.max_value || 100])[0]}
                                                            valueMax={(filters.geoFeatures?.[f.id] || [f.min_value || 0, f.max_value || 100])[1]}
                                                            onChange={(lo, hi) => handleGeoFeatureChange(f.id, [lo, hi])}
                                                            format={(n) => String(n)}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}

                                    {/* ── SORTING geo features — replaced by SortingListings ── */}
                                    {sortingGeoFeatures.length > 0 && (
                                        <>
                                            {filteringGeoFeatures.length > 0 && <div className="fp-usage-divider" />}
                                            <div className="fp-usage-section-label fp-usage-sort">
                                                <span className="fp-usage-icon">↕</span> مرتب‌سازی آگهی‌ها
                                                <span className="fp-usage-hint">بر اساس ویژگی‌های محیطی انتخابی</span>
                                            </div>
                                            <SortingListings
                                                geoFeatures={geoFeatures}
                                                sortPriorities={sortPriorities}
                                                onChange={onSortPrioritiesChange || (() => { })}
                                            />
                                        </>
                                    )}

                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    )}

                </Grid>
            </div>
        </div>
    );
};

export { buildActiveChips, CHIP_CLEAR };
export default FilterPanel;
